import { useMediaQueries } from '@react-hook/media-query'
import type { ElementsTypes } from '@vedalib/editor/lib/elements'
import { useEmbedParams } from 'hooks/useEmbedParams'
import * as R from 'ramda'
import { useEffect } from 'react'

import { MEDIA_QUERIES, SCREENS } from 'components/Editor/DeviceEmulator/constants'
import { DEVICE_ASK_ORDER } from 'components/Editor/types/data.constants'
import type { CommentMock } from 'components/comments/types'
import { notify } from 'components/uiKit/Notification'
import { NotificationType } from 'components/uiKit/Notification/types'
import type { TaskMock } from 'components/uiKit/Task'
import { EMPTY_OBJECT } from 'constants/commonConstans'
import { CommentStatus } from 'gql/__generated__/graphql'
import { BlockModeEnum } from 'gql/__generated__/graphql'
import { usePathParams, usePathQuery } from 'routes/hooks'
import { t } from 'services/Translation'

import { LmsService } from '../../lms/lms.service'
import { selectUserPreferences } from '../Users/selectors'
import { useAppDispatch, useAppSelector } from '../hooks'
import {
  init,
  reset,
  setDataProject,
  setDataSections,
  setDataBlocks,
  setDataComments,
  setDataTasks,
  setScormSettings,
  setUrlParams,
} from './actions'
import { setDeviceMode } from './actions'
import {
  DeviceMode,
  AppMode,
  ElementTab,
  PreviewMode,
  SectionTypeEnum,
  TasksTab,
  CommentsTab,
} from './enums'
import { shuffleQuestions } from './shuffleQuestions'
import { suspendDataToState } from './suspendData'
import type {
  Block,
  ElementState,
  IBlockState,
  ISectionState,
  MapType,
  Project,
  ProjectPaths,
  ScormSettings,
  Section,
} from './types'
import { getEditorSettings } from './useEditorSettings'

export const makeTestState = (section: Section) => {
  const state: ISectionState = { status: 'notStarted' }

  if (section.test?.isShuffleQuestions) {
    state.order = shuffleQuestions(section.blocks, section.test.randomCount)
  }

  return state
}
const makeSectionsState = (sections: Section[]) =>
  R.reduce(
    (map, section) =>
      section.type === 'test' ? R.mergeRight(map, { [section.id]: makeTestState(section) }) : map,
    {},
    sections,
  )

const INTERACTIVE_ELEMENTS: ElementsTypes[] = [
  'accordion',
  'card',
  'audio',
  'video',
  'labeledGraphic',
  'slider',
  'splitter',
  'tabs',
  'grouping',
  'simulator',
  'link',
  'checkbox',
  'reflection',
  'embedded',
]

const makeBlocksState = (blocks: Block[]) => {
  const blockWithStates = blocks.reduce<MapType<Partial<IBlockState>>>((acc, block) => {
    if (block.mode === BlockModeEnum.questions) {
      acc[block.uuid] = {
        incomplete: true,
        isReady: false,
        currentTry: 0,
        isMandatory: block.isMandatory,
      }
      return acc
    }
    const hasInteractiveElement = Object.values(block.elements || {}).some((el) =>
      INTERACTIVE_ELEMENTS.includes(el.type),
    )
    if (hasInteractiveElement) {
      acc[block.uuid] = { incomplete: true, isMandatory: block.isMandatory }
    }

    return acc
  }, {})

  return blockWithStates
}

const makeElementsState = (blocks: Block[]) => {
  const elements = blocks.flatMap((block) =>
    Object.values(block.elements || {}).map((element) => {
      return {
        ...element,
        blockId: block.uuid,
      }
    }),
  )

  const elementWithStates = elements.reduce<MapType<ElementState>>((acc, element) => {
    if (INTERACTIVE_ELEMENTS.includes(element.type)) {
      acc[element.id] = { incomplete: true }
    }

    return acc
  }, {})

  return elementWithStates
}
const getCurrentMatchedMedia = (
  queriesMatches: Partial<{ [key in DeviceMode]: boolean }>,
  controlledMode?: DeviceMode,
): DeviceMode => {
  for (const device of DEVICE_ASK_ORDER) {
    if (queriesMatches[device] || device === controlledMode) {
      return device
    }
  }

  return DeviceMode.desktop
}
export const useReduxUpdate = (
  path: ProjectPaths,
  mode: PreviewMode,
  project: Project,
  sections: MapType<Section>,
  blocks: MapType<Block>,
  comments: CommentMock[],
  tasks: TaskMock[],
  scormSettings?: ScormSettings,
) => {
  const { matches } = useMediaQueries(MEDIA_QUERIES)
  const dispatch = useAppDispatch()
  const deviceMode = getCurrentMatchedMedia(matches, DeviceMode.desktop)
  const params = usePathParams(path)
  const query = usePathQuery()
  const widths = useAppSelector(selectUserPreferences).editor || EMPTY_OBJECT
  const embedParams = useEmbedParams()

  const effectiveParams = {
    ...params,
    sectionId: embedParams.enabledSections?.[0] || params.sectionId,
    blockId: embedParams.enabledBlocks?.[0] || params.blockId,
  }

  useEffect(() => {
    const cb = async () => {
      // getSuspendDataSync() читает из кэша SCORM_API_Wrapper.dataStorage,
      // который прогрет в ScormInitializedRoutes до монтирования этого компонента.
      // Для non-SCORM провайдеров возвращает null немедленно (base provider).
      // Единственный dispatch(init) гарантирует, что lms.middleware не видит
      // progress_measure='0' на пустом состоянии до загрузки suspend_data.
      const suspendData = LmsService.instance.getSuspendDataSync() || undefined
      const {
        course,
        sections: stateSections,
        blocks: stateBlocks,
        elements: stateElements,
      } = suspendDataToState(
        mode === PreviewMode.scorm ? { sections, blocks } : { sections: {}, blocks: {} },
        suspendData,
      )

      const settings = getEditorSettings()

      const threadId = query.threadId || query.commentId
      const nodeId = query.nodeId
      const thread = comments.find((c) => c.id === threadId)

      if (threadId && !thread) {
        notify({
          type: NotificationType.error,
          message: t('notify.comment.noLongerExists'),
          duration: 2000,
        })
      }

      dispatch(
        init({
          urlParams: effectiveParams,
          path,
          mode,
          state: {
            comments: {
              filter: {
                resolved: thread?.status === CommentStatus.resolved ? null : CommentStatus.pending,
              },
              opened: { threadId, commentId: query.commentId },
            },
            tasks: {
              opened: { taskId: query.taskId },
            },
            mode: threadId ? AppMode.comment : mode === PreviewMode.editor ? AppMode.fill : null,
            deviceMode,
            // deviceWidth: widths[deviceMode] || SCREENS[deviceMode],
            editor: {
              highlight: null,
              selectedBlocks: params.blockId ? [params.blockId] : [],
              selectedNodes: nodeId ? [nodeId] : [],
              settings,
              navBar: {
                tab: null,
              },
              toolbar: {
                commentTab: threadId ? CommentsTab.section : null,
                elementTab: ElementTab.fill,
                taskTab: query.taskId ? TasksTab.current : null,
              },
            },
            course: R.mergeDeepRight(
              {
                status: R.values(sections)?.some(({ type }) => type === SectionTypeEnum.cover)
                  ? ('not attempted' as const)
                  : ('incomplete' as const),
                notCompletedSections: Object.values(sections)
                  .filter(
                    (section) =>
                      section.type !== SectionTypeEnum.cover &&
                      section.type !== SectionTypeEnum.chapter,
                  )
                  .map((s) => s.id),
              },
              course,
            ),
            sections: R.mergeDeepRight(makeSectionsState(R.values(sections)), stateSections),
            blocks: R.mergeDeepRight(makeBlocksState(R.values(blocks)), stateBlocks),
            elements: R.mergeDeepRight(makeElementsState(R.values(blocks)), stateElements),
          },
          data: {
            project,
            sections,
            blocks,
            comments,
          },
        }),
      )
    }
    cb()
    return () => {
      dispatch(reset())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  useEffect(() => {
    dispatch(setDataProject(project))
  }, [dispatch, project])

  useEffect(() => {
    dispatch(setDataSections(sections))
  }, [dispatch, sections])

  useEffect(() => {
    dispatch(setDataBlocks(blocks))
  }, [dispatch, blocks])

  useEffect(() => {
    dispatch(setDataComments(comments))
  }, [dispatch, comments])

  useEffect(() => {
    dispatch(setDataTasks(tasks))
  }, [dispatch, tasks])

  useEffect(() => {
    dispatch(setUrlParams(effectiveParams))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, params, embedParams.enabledSections, embedParams.enabledBlocks])

  useEffect(() => {
    const width = mode === PreviewMode.editor ? widths[deviceMode] || SCREENS[deviceMode] : '100%'
    dispatch(setDeviceMode({ mode: deviceMode, width }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, deviceMode, dispatch])

  useEffect(() => {
    dispatch(setScormSettings(scormSettings))
  }, [dispatch, scormSettings])
}
